import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { StickyNote } from '../../types';
import { summarizeConversation } from '../../utils/summarizeApi';
import { trackEvent } from '../../utils/analytics';
import { useFeatureAccess } from '../../hooks/useQuota';
import { useAuth } from '../../hooks/useAuth';
import { incrementUsage } from '../../utils/firestoreApi';

import './MergeProposalModal.css';
import { useWhiteboardStore } from '../../store/useWhiteboardStore';

interface MergeProposalModalProps {
  branchNote: StickyNote;
  parentNote: StickyNote;
  isOpen: boolean;
  onClose: () => void;
  onMerge: (summary: string, mergeTarget: StickyNote) => void;
}

const MergeProposalModal: React.FC<MergeProposalModalProps> = ({
  branchNote,
  parentNote,
  isOpen,
  onClose,
  onMerge,
}) => {
  const [summary, setSummary] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allNotes = useWhiteboardStore((state) => state.state.notes);
  const { canvasId } = useWhiteboardStore((state) => state.state);
  const { user } = useAuth();
  const { canPerformMerge } = useFeatureAccess();
  const [mergeTargetId, setMergeTargetId] = useState(parentNote.id);
  const mergeTarget = allNotes.find(n => n.id === mergeTargetId) || parentNote;

  useEffect(() => {
    setMergeTargetId(parentNote.id);
  }, [parentNote.id, isOpen]);

  const generateAISummary = useCallback(async () => {
    if (!branchNote.chatHistory || branchNote.chatHistory.length === 0) {
      setError('No chat history available for summary generation');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Use the existing summarizeConversation utility
      const result = await summarizeConversation(branchNote.chatHistory, 'conversation');
      setSummary(result.summary);
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate summary');
      setSummary('Error generating summary. Please edit manually.');
    } finally {
      setIsGenerating(false);
    }
  }, [branchNote.chatHistory]);

  // Generate AI summary when modal opens
  useEffect(() => {
    if (isOpen && branchNote.chatHistory && branchNote.chatHistory.length > 0) {
      generateAISummary();
    }
  }, [isOpen, branchNote.id, generateAISummary]);

  const handleMerge = async () => {
    if (!summary.trim()) {
      setError('Please provide a summary before merging');
      return;
    }

    // Check quota if user is authenticated and canvas ID is available
    if (user && canvasId) {
      try {
        const canMerge = await canPerformMerge();
        if (!canMerge) {
          setError('You\'ve reached your merge operation limit. Please upgrade your plan to perform more merges.');
          return;
        }
      } catch (error) {
        console.error('Error checking merge quota:', error);
        // Fallback: allow merge even if quota check fails
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      await onMerge(summary.trim(), mergeTarget);
      
      // Increment canvas quota for merge operations if user is authenticated
      if (user && canvasId) {
        try {
          await incrementUsage(user.uid, 'totalThreads');
        } catch (error) {
          console.error('Error incrementing canvas quota:', error);
        }
      }
      
      trackEvent('merge_proposal_created', { branch_name: branchNote.topic || 'Untitled' });
      onClose();
    } catch (err) {
      console.error('Error during merge:', err);
      setError(err instanceof Error ? err.message : 'Failed to merge');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateSummary = () => {
    generateAISummary();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="merge-proposal-modal-overlay" onClick={onClose}>
      <div className="merge-proposal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="merge-proposal-modal-header">
          <h2>Propose Merge</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="merge-proposal-modal-content">
          <div className="note-info">
            <div className="note-details">
              <h3>Branch Note: {branchNote.topic || 'Untitled'}</h3>
            </div>
            <div className="note-details">
              <h3>Merge into:</h3>
              <select
                className="merge-target-dropdown"
                value={mergeTargetId}
                onChange={e => setMergeTargetId(e.target.value)}
                style={{ fontSize: '1rem', padding: '6px 12px', borderRadius: 8, border: '1.5px solid #1976d2', background: '#f7f7f7', color: '#1976d2', outline: 'none', marginTop: 6 }}
              >
                {allNotes.filter(n => n.id !== branchNote.id).map(n => (
                  <option key={n.id} value={n.id}>{n.topic || 'Untitled'}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="summary-section">
            <div className="summary-header">
              <h3>Merge Summary</h3>
              <button
                className="regenerate-button"
                onClick={handleRegenerateSummary}
                disabled={isGenerating}
              >
                {isGenerating ? 'Generating...' : '🔄 Regenerate'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="summary-textarea-container">
              <textarea
                className="summary-textarea"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder={isGenerating ? 'Generating AI summary...' : 'Enter merge summary...'}
                disabled={isGenerating}
                rows={6}
              />
              <div className="summary-preview">
                <h4>Preview:</h4>
                <div className="preview-content">
                  {isGenerating ? (
                    <div className="generating-indicator">Generating summary...</div>
                  ) : summary ? (
                    <ReactMarkdown>{summary}</ReactMarkdown>
                  ) : (
                    <div className="no-summary">No summary provided</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="merge-proposal-modal-actions">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="merge-button"
              onClick={handleMerge}
              disabled={isLoading || !summary.trim()}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{
                      animation: 'spin 1s linear infinite'
                    }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Merging...
                </div>
              ) : (
                'Propose Merge'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MergeProposalModal; 